import { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { NormalizedTask } from '../../domain/normalize';
import { computeCarga, metricLabel } from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import { nameByEmail } from '../../domain/util';
import { Card, Kpi, ExportButton, BoardIntro, MetricToggle, MetricKey, tooltipStyle, axisTick, GRID, CHART_CURSOR } from './shared';

export default function BoardEquipo({ tasks, users }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const [loadMetric, setLoadMetric] = useState<MetricKey>('horas');
  const carga = useMemo(() => computeCarga(tasks).map((c) => ({ ...c, nombre: nameByEmail(c.responsable, users) })), [tasks, users]);
  const cargaPorResponsable = useMemo(
    () => carga.map((c) => ({ ...c, valor: loadMetric === 'horas' ? c.horas : c.tareas })),
    [carga, loadMetric]
  );

  const totalHoras = carga.reduce((s, c) => s + c.horas, 0);
  const activos = carga.length;
  const horasProm = activos ? Math.round((totalHoras / activos) * 10) / 10 : 0;
  const tareasProm = activos ? Math.round((carga.reduce((s, c) => s + c.tareas, 0) / activos) * 10) / 10 : 0;
  const maxBacklog = [...carga].sort((a, b) => b.enCurso - a.enCurso)[0];
  const maxVencidas = [...carga].sort((a, b) => b.vencidas - a.vencidas)[0];
  const maxUrg = [...carga].sort((a, b) => b.urgentes - a.urgentes)[0];

  const csvRows = carga.map((c) => ({
    responsable: c.nombre, tareas: c.tareas, en_curso: c.enCurso, vencidas: c.vencidas,
    proximas: c.proximas, urgentes: c.urgentes, horas: c.horas, pct_horas: c.pctHoras,
  }));

  return (
    <div className="grid">
      <BoardIntro
        titulo="Carga del equipo"
        objetivo="Cómo se reparte el trabajo entre las personas: quién tiene más tareas, más horas y más cosas por vencer."
        preguntas={['¿Está parejo el reparto?', '¿Quién está más cargado?', '¿Hay alguien con muchas vencidas?']}
      />
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Responsables activos" value={String(activos)} help="Personas con al menos una tarea asignada en el período." />
        <Kpi label="Horas prom./persona" value={String(horasProm)} />
        <Kpi label="Tareas prom./persona" value={String(tareasProm)} />
      </div>

      {activos > 0 && (
        <div className="panel callout">
          <span className="mini-label">Dónde se concentra la carga</span>
          <p>
            {maxBacklog && maxBacklog.enCurso > 0
              ? <>El mayor backlog lo tiene <span className="hl hl-accent">{maxBacklog.nombre}</span> con <strong>{maxBacklog.enCurso}</strong> {maxBacklog.enCurso === 1 ? 'tarea' : 'tareas'} en curso. </>
              : 'No hay tareas en curso. '}
            {maxVencidas && maxVencidas.vencidas > 0
              ? <>Las más vencidas se acumulan en <span className="hl hl-danger">{maxVencidas.nombre}</span> (<strong>{maxVencidas.vencidas}</strong>). </>
              : 'Nadie tiene tareas vencidas. '}
            {maxUrg && maxUrg.urgentes > 0
              ? <>Y quien más urgentes tiene es <span className="hl hl-warn">{maxUrg.nombre}</span> (<strong>{maxUrg.urgentes}</strong>).</>
              : 'No hay tareas urgentes.'}
          </p>
        </div>
      )}

      <Card
        title={`Carga por responsable (${metricLabel(loadMetric).toLowerCase()})`}
        hint="Una sola unidad por eje: alterná entre horas o cantidad de tareas."
        action={<MetricToggle value={loadMetric} onChange={setLoadMetric} />}
      >
        <ResponsiveContainer width="100%" height={Math.max(300, carga.length * 36 + 40)}>
          <BarChart data={cargaPorResponsable} layout="vertical" margin={{ left: 10, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" allowDecimals={loadMetric === 'horas'} tick={axisTick} />
            <YAxis type="category" dataKey="nombre" tick={axisTick} width={140} interval={0} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="valor" name={metricLabel(loadMetric)} fill={loadMetric === 'horas' ? '#15803d' : '#4f46e5'} maxBarSize={22} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Estado de tareas por responsable">
        <ResponsiveContainer width="100%" height={Math.max(300, carga.length * 36 + 40)}>
          <BarChart data={carga} layout="vertical" margin={{ left: 10, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={axisTick} />
            <YAxis type="category" dataKey="nombre" tick={axisTick} width={140} interval={0} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="enCurso" name="En curso" stackId="s" fill="#2563eb" maxBarSize={20} />
            <Bar dataKey="proximas" name="Próximas" stackId="s" fill="#f59e0b" maxBarSize={20} />
            <Bar dataKey="vencidas" name="Vencidas" stackId="s" fill="#b91c1c" maxBarSize={20} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Tabla operativa por responsable" action={<ExportButton filename="equipo.csv" rows={csvRows} />}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Responsable</th><th className="num">Tareas</th><th className="num">En curso</th><th className="num">Vencidas</th><th className="num">Próximas</th><th className="num">Urgentes</th><th className="num">Horas</th><th className="num">% horas</th></tr></thead>
            <tbody>
              {carga.map((c) => (
                <tr key={c.responsable}>
                  <td>{c.nombre}</td><td className="num">{c.tareas}</td><td className="num">{c.enCurso}</td>
                  <td className="num" style={c.vencidas ? { color: 'var(--danger)', fontWeight: 600 } : {}}>{c.vencidas}</td>
                  <td className="num">{c.proximas}</td><td className="num">{c.urgentes}</td>
                  <td className="num">{c.horas}</td><td className="num">{c.pctHoras}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>La carga se muestra en totales absolutos (horas y tareas asignadas), no como porcentaje de la capacidad de cada persona: todavía no se registran las horas disponibles de cada responsable.</div>
      </Card>
    </div>
  );
}
